import { useNavigate } from "@tanstack/react-router";
import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getNewTokens, updateLogoutTime } from "~/services/auth.services";
import { decryptData, encryptData } from "~/utils/crypto";
import SITE_CONFIG from "~/configs/site-config";

interface IAuthProps {
  idToken?: string | null;
  refreshToken?: string | null;
  loading?: boolean;
  active?: boolean;
  remainingTrialDays?: number;
  totalPagesCount?: number;
  roles?: string[];
  loginHandler: Dispatch<SetStateAction<any | null>>;
  logout: Dispatch<SetStateAction<any | null>>;
  showLoader: Dispatch<SetStateAction<any | null>>;
  hideLoader: Dispatch<SetStateAction<any | null>>;
  inactivateUser: Dispatch<SetStateAction<any | null>>;
  setRemainingTrialDays: Dispatch<SetStateAction<any | null>>;
  setTotalPages: Dispatch<SetStateAction<any | null>>;
  setRoles: Dispatch<SetStateAction<string[]>>;
  updateRefreshToken?: any;
}

const AuthContext = createContext<IAuthProps>({
  loginHandler: () => {},
  logout: () => {},
  showLoader: () => {},
  hideLoader: () => {},
  inactivateUser: () => {},
  setRemainingTrialDays: () => {},
  setTotalPages: () => {},
  setRoles: () => {},
} satisfies IAuthProps);

export const AuthContextProvider = ({ children }: { children: ReactNode }) => {
  const [userToken, setUserToken] = useState<string | null>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [remainingDays, setRemainingDays] = useState<number>(-1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [refreshToken, setRefreshToken] = useState<string | null>();
  const [roles, setRoles] = useState<string[]>([]);
  const navigate = useNavigate();

  // Handle login to set token
  const loginHandler = (token: string) => {
    setUserToken(token);
  };
  // Check if token is expired or about to expire
  const isTokenValid = useCallback(() => {
    try {
      const token_expiry = localStorage.getItem("token_expiry");
      if (!token_expiry) return false;
      const expiryTimeStr = token_expiry;
      if (!expiryTimeStr) {
        // If no expiry time is stored, assume token needs refresh
        return false;
      }

      const expiryTime = parseInt(expiryTimeStr, 10) * 1000; // Convert to milliseconds
      const currentTime = Date.now();

      // Consider token expired if it's within 5 minutes of expiry
      const fiveMinutesInMs = 5 * 60 * 1000;
      console.log(
        "Time check:",
        new Date(currentTime).toISOString(),
        "vs expiry:",
        new Date(expiryTime).toISOString()
      );
      return currentTime + fiveMinutesInMs < expiryTime;
    } catch (error) {
      console.error("Error checking token expiry:", error);
      return false; // Assume token needs refresh if there's an error
    }
  }, []);

  useEffect(() => {
    async function refreshToken() {
      console.log("refreshToken function called at:", new Date().toISOString());
      // Only refresh if token is expired or about to expire
      if (isTokenValid()) {
        console.log("Token not expired yet, skipping refresh");
        return;
      }

      try {
        const {
          data,
          status,
          message,
        }: { data?: any; status: boolean; message?: string } =
          await getNewTokens();
        const token = localStorage.getItem("id_token");
        if (status && data && token != null) {
          localStorage.setItem("id_token", encryptData(data.id_token));

          const tokenParts = data.id_token.split(".");
          if (tokenParts.length !== 3) {
            console.error("Invalid token format");
            return;
          }

          // Parse and validate token payload
          const payload = JSON.parse(atob(tokenParts[1]));

          // Get the expiry time directly from the token
          const expiryTime = payload["exp"];

          if (expiryTime) {
            localStorage.setItem("token_expiry", expiryTime.toString());
            console.log(
              "Token refreshed, new expiry:",
              new Date(expiryTime * 1000).toISOString()
            );
          }
        } else {
          console.warn("Failed to refresh token:", message);
          // Don't logout immediately, let the next refresh attempt try again
        }
      } catch (error) {
        console.log("Error refreshing token:", error);
      }
    }

    // Check token expiry every 5 minutes
    const tokenCheckInterval = setInterval(
      () => {
        void refreshToken();
      },
      3 * 60 * 1000
    );

    // Initial token refresh when component mounts
    void refreshToken();

    return () => {
      clearInterval(tokenCheckInterval);
    };
  }, [isTokenValid]);

  useEffect(() => {
    if (userToken == null) {
      const encryptedToken = localStorage.getItem("id_token");
      if (encryptedToken) {
        try {
          const decryptedToken = decryptData(encryptedToken);
          if (decryptedToken) {
            // Validate token format before setting
            const parts = decryptedToken.split(".");
            if (parts.length === 3) {
              try {
                // Check if payload is valid JSON
                JSON.parse(atob(parts[1]));
                setUserToken(decryptedToken);
              } catch (e) {
                console.error("Invalid token payload:", e);
                localStorage.removeItem("id_token");
                navigate({ to: "/" });
              }
            } else {
              console.error("Invalid token format");
              localStorage.removeItem("id_token");
              navigate({ to: "/" });
            }
          }
        } catch (error) {
          console.error("Error decrypting token:", error);
          localStorage.removeItem("id_token");
          navigate({ to: "/" });
        }
      }
    }
    if (userToken != null && userToken !== "") {
      try {
        const parts = userToken.split(".");
        if (parts.length === 3) {
          // Verify it's a valid JWT token
          const payload = JSON.parse(atob(parts[1]));
          if ("cognito:groups" in payload) {
            setRoles(payload["cognito:groups"] as string[]);
          }
        }
      } catch (error) {
        console.error("Error parsing token payload:", error);
        // Handle invalid token
        setUserToken(null);
        localStorage.removeItem("id_token");
        navigate({ to: "/" });
      }
    }
  }, [navigate, userToken]);

  // Set refresh token
  const updateRefreshToken = (token: string) => {
    setRefreshToken(token);
  };

  // Handle login to remove token
  const logoutHandler = useCallback(() => {
    if (localStorage.getItem("logging_out")) {
      return;
    }
    // Clear user token
    setUserToken(null);

    localStorage.clear();
    sessionStorage.clear();

    // Create a new temporary channel for each logout
    try {
      const channel = new BroadcastChannel("logout_channel");
      channel.postMessage("logout");
      channel.close();
    } catch (error) {
      console.log("BroadcastChannel error: ", error);
    }

    // window.location.reload();
    navigate({
      to: "/",
      replace: true,
      search: {
        t: Date.now(),
      },
    });
  }, [navigate]);

  // Handle show loader state
  const showLoaderHandler = () => {
    setIsLoading(true);
  };
  // Handle hide loader state
  const hideLoaderHandler = () => {
    setIsLoading(false);
  };
  // Handle inactive user state
  const inactiveUser = () => {
    setIsActive(false);
  };
  // Handle remaining days state
  const remainingDaysHandler = (days: number) => {
    setRemainingDays(days);
  };
  // Handle total pages count
  const totalPagesHandler = (count: number) => {
    setTotalPages(count);
  };

  const contextValue = useMemo(
    () => ({
      idToken: userToken,
      loading: isLoading,
      active: isActive,
      refreshToken,
      remainingTrialDays: remainingDays,
      totalPagesCount: totalPages,
      setRefreshToken: updateRefreshToken,
      loginHandler,
      logout: logoutHandler,
      showLoader: showLoaderHandler,
      hideLoader: hideLoaderHandler,
      inactivateUser: inactiveUser,
      setRemainingTrialDays: remainingDaysHandler,
      setTotalPages: totalPagesHandler,
      setRoles,
      roles,
    }),
    [
      userToken,
      isLoading,
      isActive,
      refreshToken,
      remainingDays,
      totalPages,
      logoutHandler,
      roles,
    ]
  );

  // Change the lastActivityTime tracking to use localStorage
  const getLastActivityTime = () => {
    return parseInt(
      localStorage.getItem("lastActivityTime") || Date.now().toString()
    );
  };

  const setLastActivityTime = () => {
    localStorage.setItem("lastActivityTime", Date.now().toString());
  };

  const checkInactivity = useCallback(async () => {
    console.log(`checkInactivity called: ${new Date().toLocaleTimeString()}`); // Log the current time
    const currentTime = Date.now();
    const timeSinceLastActivity = currentTime - getLastActivityTime();

    // 30 minutes in milliseconds
    if (timeSinceLastActivity > SITE_CONFIG.INACTIVITY_TIMEOUT.TIMEOUT) {
      console.log("if block: ", localStorage.getItem("id_token"));
      if (!localStorage.getItem("id_token")) return;
      const { status } = await updateLogoutTime();
      if (status) {
        console.log("Token updated, logout triggered");
        logoutHandler();
      }
    }
  }, [logoutHandler]);

  // Update last activity time when API calls are made, excluding auth-related calls
  const handleApiCall = useCallback((event: Event) => {
    // Check if the event has custom detail with URL information
    const customEvent = event as CustomEvent<{ url?: string }>;
    if (customEvent.detail && customEvent.detail.url) {
      // Skip updating activity time for auth-related endpoints
      const url = customEvent.detail.url;
      const ignoredURLs = [
        "/oauth2/token",
        process.env.VITE_AWS_COGNITO_URL,
        process.env.VITE_AWS_ROLE_UPDATE_LOGOUT_URL,
        process.env.VITE_AWS_WORKLIST_COUNTERS,
      ];
      if (ignoredURLs.some((ignoreURL) => url.includes(ignoreURL!))) {
        console.log("Auth-related API call, not updating activity time:", url);
        return;
      }
    }
    setLastActivityTime();
  }, []);

  // Update last activity time when user interacts with the page
  const handleUserActivity = useCallback((event: Event) => {
    if (
      event.type === "click" ||
      event.type === "keydown" ||
      event.type === "touchstart"
    ) {
      console.log("handleUserActivity");
      setLastActivityTime();
    }
  }, []);

  useEffect(() => {
    // Set up interval to check inactivity every minute
    let intervalId = null;
    // Create a new channel for listening
    const channel = new BroadcastChannel("logout_channel");
    channel.onmessage = () => {
      if (!localStorage.getItem("logging_out")) {
        localStorage.setItem("logging_out", "true");
        navigate({
          to: "/",
          replace: true,
          search: {
            t: Date.now(),
          },
        });
      }
    };
    setLastActivityTime();

    if (SITE_CONFIG.INACTIVITY_TIMEOUT.ENABLED) {
      intervalId = setInterval(
        checkInactivity,
        SITE_CONFIG.INACTIVITY_TIMEOUT.INTERVAL
      );
    }

    window.addEventListener("api-call", handleApiCall as EventListener);
    window.addEventListener("keydown", handleUserActivity);
    window.addEventListener("click", handleUserActivity);
    window.addEventListener("touchstart", handleUserActivity);

    const originalFetch = window.fetch;
    window.fetch = function (...args) {
      let url = "";
      if (typeof args[0] === "string") {
        url = args[0];
      } else if (args[0] instanceof URL) {
        url = args[0].href;
      } else if (args[0] instanceof Request) {
        url = args[0].url;
      }

      const customEvent = new CustomEvent("api-call", {
        detail: { url },
      });
      handleApiCall(customEvent);
      return originalFetch.apply(window, args);
    };

    return () => {
      if (intervalId != null) {
        clearInterval(intervalId);
      }
      window.fetch = originalFetch;
      window.removeEventListener("api-call", handleApiCall);
      window.removeEventListener("keydown", handleUserActivity);
      window.removeEventListener("click", handleUserActivity);
      window.removeEventListener("touchstart", handleUserActivity);
      channel.close(); // Close the listening channel
    };
  }, [
    checkInactivity,
    handleApiCall,
    handleUserActivity,
    logoutHandler,
    navigate,
  ]);

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export default AuthContext;
export function useAuth() {
  return useContext(AuthContext);
}
